import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import {
  RegulatoryStatusOption,
  TotalQuantityByTsdfQuery,
  WasteFacilitiesByGeneratorStatusAndStateQuery,
  WasteManagementMethodQuery,
  WasteTotalWasteQuery,
  WasteVendorTotalsQuery,
} from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useTransition,
} from "react";
import invariant from "tiny-invariant";

import {
  FACILITIES_BY_GENERATOR_STATUS_AND_STATE_QUERY,
  MANAGEMENT_METHOD_QUERY,
  TSDF_TOTAL_QUANTITY_QUERY,
  WASTE_TOTAL_WASTE_QUERY,
  WASTE_VENDOR_TOTALS_QUERY,
} from "./schema";
import { ALL_GENERATOR_STATUS_OPTIONS, US_STATES } from "./types";

type Filter = {
  shipDateStart?: string;
  shipDateEnd?: string;
  currentGeneratorStatuses?: RegulatoryStatusOption[];
  states?: string[];
  facilityTagIds?: string[];
  wasteLineTagIds?: string[];
};

export const wasteOverviewDefaultFilters: Filter = {
  shipDateStart: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
  shipDateEnd: new Date().toISOString().slice(0, 10),
  currentGeneratorStatuses: [...ALL_GENERATOR_STATUS_OPTIONS],
  states: [...US_STATES],
  facilityTagIds: [],
  wasteLineTagIds: [],
};

export type WasteOverviewState = {
  filters: Partial<Filter>;
  isPending: boolean;
};

type WasteOverviewContextType = {
  overviewState: WasteOverviewState;
  setOverviewState: (cb: (state: Partial<Filter>) => void) => void;
  facilitiesByLocationAndGeneratorStatusQuery: QueryRef<WasteFacilitiesByGeneratorStatusAndStateQuery>;
  totalWasteQuery: QueryRef<WasteTotalWasteQuery>;
  tsdfQuery: QueryRef<TotalQuantityByTsdfQuery>;
  vendorQuery: QueryRef<WasteVendorTotalsQuery>;
  managementMethodQuery: QueryRef<WasteManagementMethodQuery>;
};

const MAX_VISIBLE_CHART_ITEMS = 20;

const WasteOverviewStateContext = createContext<WasteOverviewContextType | undefined>(undefined);

export function useWasteOverviewState() {
  const context = useContext(WasteOverviewStateContext);
  if (!context) {
    throw new Error("useWasteOverviewState must be used within a WasteOverviewContext");
  }
  return context;
}

export function WasteOverviewContext({ children }: { children: ReactNode }) {
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId is required");

  const { filters, setFilters } = useQueryParams<Filter>();
  const [isPending, startTransition] = useTransition();

  // This is a combination of the url filter state and the pending suspense state
  const overviewState = useMemo(() => ({ filters, isPending }), [filters, isPending]);

  // This is a way to update the mutable state
  const setOverviewState = useCallback(
    (cb: (state: Partial<Filter>) => void) => {
      startTransition(() => {
        setFilters((f) => {
          cb(f);
        });
      });
    },
    [setFilters]
  );

  const hasSetDefaults = useRef(false);

  useEffect(() => {
    if (hasSetDefaults.current) return;

    const hasAnyQuery = Object.values(filters).some((v) => v !== undefined);
    if (!hasAnyQuery) {
      setFilters((state) => ({
        ...state,
        ...wasteOverviewDefaultFilters,
      }));
      hasSetDefaults.current = true;
    }
  }, [filters, setFilters]);

  const facilitiesByLocationAndGeneratorStatusFilters = useMemo(() => {
    if (Object.keys(filters).length === 0) {
      return {
        shipDateStart: wasteOverviewDefaultFilters.shipDateStart,
        shipDateEnd: wasteOverviewDefaultFilters.shipDateEnd,
        states: wasteOverviewDefaultFilters.states,
        currentGeneratorStatuses: wasteOverviewDefaultFilters.currentGeneratorStatuses,
        facilityTagIds: wasteOverviewDefaultFilters.facilityTagIds,
        wasteLineTagIds: wasteOverviewDefaultFilters.wasteLineTagIds,
      };
    } else {
      return {
        shipDateStart: filters.shipDateStart,
        shipDateEnd: filters.shipDateEnd,
        states: filters.states,
        // Always query all generator statuses for the facilities by location and generator status chart
        // unless the generator status filter is empty
        currentGeneratorStatuses: filters.currentGeneratorStatuses?.length
          ? [...ALL_GENERATOR_STATUS_OPTIONS]
          : [],
        facilityTagIds: filters.facilityTagIds ?? [],
        wasteLineTagIds: filters.wasteLineTagIds ?? [],
      };
    }
  }, [filters]);

  const [facilitiesByLocationAndGeneratorStatusQuery] = useBackgroundQuery(
    FACILITIES_BY_GENERATOR_STATUS_AND_STATE_QUERY,
    {
      variables: {
        tenantId: tenantId ?? "",
        filter: facilitiesByLocationAndGeneratorStatusFilters,
      },
    }
  );

  const chartsFilters = useMemo(() => {
    if (Object.keys(filters).length === 0) {
      return wasteOverviewDefaultFilters;
    } else {
      return {
        shipDateStart: filters.shipDateStart,
        shipDateEnd: filters.shipDateEnd,
        states: filters.states,
        currentGeneratorStatuses: filters.currentGeneratorStatuses,
        facilityTagIds: filters.facilityTagIds ?? [],
        wasteLineTagIds: filters.wasteLineTagIds ?? [],
      };
    }
  }, [filters]);

  const [totalWasteQuery] = useBackgroundQuery(WASTE_TOTAL_WASTE_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: chartsFilters,
    },
  });

  const [tsdfQuery] = useBackgroundQuery(TSDF_TOTAL_QUANTITY_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: chartsFilters,
      maxResults: MAX_VISIBLE_CHART_ITEMS,
    },
  });

  const [vendorQuery] = useBackgroundQuery(WASTE_VENDOR_TOTALS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: chartsFilters,
      limit: MAX_VISIBLE_CHART_ITEMS,
    },
  });

  const [managementMethodQuery] = useBackgroundQuery(MANAGEMENT_METHOD_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: chartsFilters,
      limit: MAX_VISIBLE_CHART_ITEMS,
    },
  });

  const value = useMemo(
    () => ({
      overviewState,
      setOverviewState,
      facilitiesByLocationAndGeneratorStatusQuery,
      totalWasteQuery,
      tsdfQuery,
      vendorQuery,
      managementMethodQuery,
    }),
    [
      overviewState,
      setOverviewState,
      facilitiesByLocationAndGeneratorStatusQuery,
      totalWasteQuery,
      tsdfQuery,
      vendorQuery,
      managementMethodQuery,
    ]
  );

  return (
    <WasteOverviewStateContext.Provider value={value}>
      {children}
    </WasteOverviewStateContext.Provider>
  );
}
